import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ChatInterface = ({ messages, onSendMessage, currentUser, activeUsers }) => {
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = (e) => {
    e?.preventDefault();
    if (newMessage?.trim()) {
      onSendMessage({
        id: Date.now(),
        content: newMessage?.trim(),
        sender: currentUser?.name,
        senderId: currentUser?.id,
        timestamp: new Date(),
        type: 'text'
      });
      setNewMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage(e);
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - messageTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return messageTime?.toLocaleDateString();
  };

  const renderMessage = (message) => {
    const isCurrentUser = message?.senderId === currentUser?.id;
    const senderInfo = activeUsers?.find(user => user?.id === message?.senderId) || 
                      { name: message?.sender, avatar: `https://api.dicebear.com/7.x/initials/svg?seed=${message?.sender}` };

    return (
      <div key={message?.id} className={`flex gap-3 ${isCurrentUser ? 'flex-row-reverse' : ''}`}>
        <div className="flex-shrink-0">
          <Image
            src={senderInfo?.avatar}
            alt={senderInfo?.name}
            className="w-8 h-8 rounded-full object-cover"
          />
        </div>
        <div className={`flex-1 max-w-xs sm:max-w-md ${isCurrentUser ? 'text-right' : ''}`}>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-foreground">
              {isCurrentUser ? 'You' : senderInfo?.name}
            </span>
            <span className="text-xs text-muted-foreground">
              {formatTimestamp(message?.timestamp)}
            </span>
          </div>
          
          <div className={`inline-block p-3 rounded-lg ${
            isCurrentUser 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-muted text-foreground'
          }`}>
            {message?.type === 'text' ? (
              <p className="text-sm whitespace-pre-wrap">{message?.content}</p>
            ) : message?.type === 'system' ? (
              <p className="text-xs italic">{message?.content}</p>
            ) : message?.type === 'file' ? (
              <div className="flex items-center gap-2">
                <Icon name="Paperclip" size={16} />
                <span className="text-sm">{message?.fileName}</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg flex flex-col h-96">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="MessageSquare" size={20} />
          Team Chat
        </h2>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
          <span>{activeUsers?.length} online</span>
        </div>
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.map(renderMessage)}
        
        {/* Typing Indicator */}
        {typingUsers?.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="flex gap-1">
              <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" />
              <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
              <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
            </div>
            <span>
              {typingUsers?.length === 1 
                ? `${typingUsers?.[0]} is typing...` 
                : `${typingUsers?.length} people are typing...`
              }
            </span>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      {/* Input */}
      <form onSubmit={handleSendMessage} className="p-4 border-t border-border">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e?.target?.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message... (Enter to send, Shift+Enter for new line)"
              className="w-full p-3 pr-12 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              rows={1}
              style={{ minHeight: '44px', maxHeight: '120px' }}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              iconName="Paperclip"
              className="absolute right-2 top-2"
              onClick={() => {/* Handle file attachment */}}
            />
          </div>
          <Button
            type="submit"
            variant="default"
            size="sm"
            iconName="Send"
            disabled={!newMessage?.trim()}
            className="self-end"
          />
        </div>
      </form>
    </div>
  );
};

export default ChatInterface;